import type { AuthError as SupabaseAuthError, Session } from '@supabase/supabase-js'
import { supabase } from '@/lib/supabase'
import api from './api'
import type { User } from '@/types'

export type AuthErrorCode =
  | 'email_taken'
  | 'weak_password'
  | 'email_invalid'
  | 'rate_limited'
  | 'network'
  | 'unknown'

/** Typed error raised by the auth service so the UI can map to i18n keys. */
export class AuthError extends Error {
  constructor(public readonly code: AuthErrorCode, message?: string) {
    super(message ?? code)
    this.name = 'AuthError'
  }
}

function isSupabaseAuthError(err: unknown): err is SupabaseAuthError {
  return typeof err === 'object' && err !== null && 'message' in err
}

/**
 * Normalize Supabase signup/resend errors into a stable `AuthError.code`
 * so RegisterPage can map them to dedicated i18n messages.
 */
export function mapAuthError(err: unknown): AuthError {
  if (err instanceof AuthError) return err

  if (!isSupabaseAuthError(err)) {
    // Network / fetch failure (no response from Supabase)
    if (err instanceof TypeError && /fetch/i.test(err.message)) {
      return new AuthError('network', err.message)
    }
    return new AuthError('unknown', err instanceof Error ? err.message : String(err))
  }

  const code = (err as SupabaseAuthError & { code?: string }).code ?? ''
  const message = err.message ?? ''
  const lower = `${code} ${message}`.toLowerCase()

  if (code === 'user_already_exists' || /already registered|already exists/.test(lower)) {
    return new AuthError('email_taken', message)
  }
  if (code === 'weak_password' || /weak.password|at least \d+ char|password.*short/.test(lower)) {
    return new AuthError('weak_password', message)
  }
  if (
    code === 'email_address_invalid' ||
    code === 'validation_failed' ||
    /invalid.*email|email.*invalid/.test(lower)
  ) {
    return new AuthError('email_invalid', message)
  }
  if (
    code === 'over_email_send_rate_limit' ||
    code === 'over_request_rate_limit' ||
    /rate.?limit|too many requests/.test(lower)
  ) {
    return new AuthError('rate_limited', message)
  }
  return new AuthError('unknown', message)
}

interface RegisterPayload {
  email: string
  password: string
  first_name: string
  last_name: string
}

/**
 * Build the absolute URL Supabase should redirect to after the user clicks
 * the email link. Must match a "Redirect URL" allowlist entry in the Supabase
 * project — otherwise the link lands on Supabase's default Site URL (often
 * `http://127.0.0.1:54321` in local dev), which then trips CORS or 404.
 *
 * In dev-proxy setup → `http://frontend.localhost/auth/callback`.
 * In prod          → `https://kilometre-zero.app/auth/callback`.
 */
function authCallbackUrl(query = ''): string {
  const base = import.meta.env.VITE_APP_URL ?? window.location.origin
  return `${base}/auth/callback${query}`
}

export const authService = {
  async login(email: string, password: string): Promise<void> {
    const { error } = await supabase.auth.signInWithPassword({ email, password })
    if (error) throw mapAuthError(error)
  },

  async register(
    payload: RegisterPayload,
  ): Promise<{ emailConfirmationRequired: boolean }> {
    const { email, password, first_name, last_name } = payload
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: { first_name, last_name },
        emailRedirectTo: authCallbackUrl(),
      },
    })
    if (error) throw mapAuthError(error)

    if (!data.session) {
      // Email confirmation required — profile will be created on first SIGNED_IN event
      return { emailConfirmationRequired: true }
    }

    await api.post<User>(
      '/users/',
      { email, first_name, last_name },
      { headers: { Authorization: `Bearer ${data.session.access_token}` } },
    )
    return { emailConfirmationRequired: false }
  },

  async resendConfirmation(email: string): Promise<void> {
    const { error } = await supabase.auth.resend({
      type: 'signup',
      email,
      options: { emailRedirectTo: authCallbackUrl() },
    })
    if (error) throw mapAuthError(error)
  },

  async createProfileFromSession(session: Session): Promise<void> {
    const { data: { user: supabaseUser } } = await supabase.auth.getUser()
    if (!supabaseUser) return
    const metadata = supabaseUser.user_metadata ?? {}
    let firstName: string = metadata.first_name ?? ''
    let lastName: string = metadata.last_name ?? ''
    // Backwards compat: legacy accounts only stored full_name.
    if (!firstName && typeof metadata.full_name === 'string' && metadata.full_name.trim()) {
      const [head, ...rest] = metadata.full_name.trim().split(/\s+/)
      firstName = head
      lastName = rest.join(' ')
    }
    if (!firstName) {
      // Last resort: derive a placeholder from the email so the (NOT NULL) constraint passes.
      firstName = supabaseUser.email?.split('@')[0] ?? 'user'
    }
    await api.post<User>(
      '/users/',
      {
        email: supabaseUser.email,
        first_name: firstName,
        last_name: lastName,
      },
      { headers: { Authorization: `Bearer ${session.access_token}` } },
    )
  },

  async me(accessToken?: string): Promise<User> {
    const headers = accessToken ? { Authorization: `Bearer ${accessToken}` } : undefined
    const { data } = await api.get<User>('/me', { headers })
    return data
  },

  async sendPasswordReset(email: string): Promise<void> {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: authCallbackUrl('?type=recovery'),
    })
    if (error) throw mapAuthError(error)
  },

  async updatePassword(newPassword: string): Promise<void> {
    const { error } = await supabase.auth.updateUser({ password: newPassword })
    if (error) throw mapAuthError(error)
  },

  async signOut(): Promise<void> {
    await supabase.auth.signOut()
  },
}
